import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { PATIENT_PANEL_THEME } from '../patientPanelTheme';

type ActivityState = 'active' | 'inactive' | 'inactivated';

interface PatientActivityStatus {
  status: ActivityState;
  inactivated_at: string | null;
  active_plans: Array<{ id: string; plan_name: string | null }>;
  available_sessions: number;
  session_packages: Array<{ ticket_id: string; procedure_name: string | null; available: number }>;
  future_appointments_count: number;
  future_appointments: Array<{ id: string; appointment_date: string; start_time: string | null; professional_name: string | null }>;
}

interface PatientActivityStatusChipProps {
  patientId: string;
  reloadToken?: number;
  onChanged?: () => void;
}

const CHIP_META: Record<ActivityState, { label: string; bg: string; color: string; tip: string }> = {
  active: {
    label: 'ATIVO',
    bg: PATIENT_PANEL_THEME.tealSoft,
    color: PATIENT_PANEL_THEME.tealDark,
    tip: 'Clique para marcar como INATIVO',
  },
  inactive: {
    label: 'INATIVO',
    bg: PATIENT_PANEL_THEME.mutedAlt,
    color: PATIENT_PANEL_THEME.slate,
    tip: 'Sem plano ativo, sessões a executar ou agendamento futuro. Clique para marcar como ATIVO',
  },
  inactivated: {
    label: 'INATIVADO',
    bg: '#fef2f2',
    color: '#b91c1c',
    tip: 'Paciente inativado manualmente. Clique para marcar como ATIVO',
  },
};

const formatDate = (ymd: string) => {
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : ymd;
};

const PatientActivityStatusChip: React.FC<PatientActivityStatusChipProps> = ({ patientId, reloadToken, onChanged }) => {
  const [data, setData] = useState<PatientActivityStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteAppointments, setDeleteAppointments] = useState<'delete' | 'keep' | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await axios.get(getApiUrl(`/clinic/patients/${patientId}/activity-status`));
      setData(res.data?.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { void load(); }, [load, reloadToken]);

  const openDialog = () => {
    setError('');
    setDeleteAppointments('');
    setDialogOpen(true);
  };

  const willInactivate = data?.status === 'active';
  const hasFutureAppointments = (data?.future_appointments_count || 0) > 0;
  const canConfirm = !willInactivate || !hasFutureAppointments || deleteAppointments !== '';

  const handleConfirm = async () => {
    if (!data) return;
    setSaving(true);
    setError('');
    try {
      if (willInactivate) {
        const res = await axios.post(getApiUrl(`/clinic/patients/${patientId}/inactivate`), {
          delete_future_appointments: deleteAppointments === 'delete',
        });
        setData(res.data?.data?.status || null);
      } else {
        const res = await axios.post(getApiUrl(`/clinic/patients/${patientId}/reactivate`));
        setData(res.data?.data || null);
      }
      setDialogOpen(false);
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível alterar o status do paciente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <Chip size="small" label="…" sx={{ height: 24, borderRadius: '10px', fontSize: '0.7rem' }} />;
  }
  if (!data) return null;

  const meta = CHIP_META[data.status];
  const planCount = data.active_plans.length;

  return (
    <>
      <Tooltip title={meta.tip} arrow>
        <Chip
          label={meta.label}
          size="small"
          onClick={openDialog}
          sx={{
            bgcolor: meta.bg,
            color: meta.color,
            fontWeight: 800,
            letterSpacing: '0.04em',
            fontSize: '0.7rem',
            height: 24,
            borderRadius: '10px',
            cursor: 'pointer',
            '&:hover': { bgcolor: meta.bg, filter: 'brightness(0.96)' },
          }}
        />
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => (saving ? null : setDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {willInactivate ? 'Marcar paciente como INATIVO' : 'Marcar paciente como ATIVO'}
        </DialogTitle>
        <DialogContent dividers>
          {willInactivate ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Ao confirmar, o sistema vai:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2, '& li': { mb: 0.5, fontSize: '0.875rem' } }}>
                <li>
                  {planCount > 0
                    ? `Cancelar ${planCount} plano(s) ativo(s) (${data.active_plans.map((p) => p.plan_name || 'Plano').join(', ')}) e as mensalidades em aberto`
                    : 'Nenhum plano ativo para cancelar'}
                </li>
                <li>
                  {data.available_sessions > 0
                    ? `Remover ${data.available_sessions} sessão(ões) disponível(is) dos pacotes (${data.session_packages.map((p) => `${p.procedure_name || 'Procedimento'}: ${p.available}`).join(', ')})`
                    : 'Nenhuma sessão disponível para remover'}
                </li>
              </Box>

              {hasFutureAppointments && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    O paciente tem {data.future_appointments_count} agendamento(s) futuro(s). Deseja excluir todos?
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1, color: PATIENT_PANEL_THEME.slate, '& li': { fontSize: '0.8rem' } }}>
                    {data.future_appointments.map((appt) => (
                      <li key={appt.id}>
                        {formatDate(appt.appointment_date)}{appt.start_time ? ` às ${appt.start_time}` : ''}
                        {appt.professional_name ? ` · ${appt.professional_name}` : ''}
                      </li>
                    ))}
                    {data.future_appointments_count > data.future_appointments.length && (
                      <li>e mais {data.future_appointments_count - data.future_appointments.length}…</li>
                    )}
                  </Box>
                  <RadioGroup
                    value={deleteAppointments}
                    onChange={(e) => setDeleteAppointments(e.target.value as 'delete' | 'keep')}
                  >
                    <FormControlLabel value="delete" control={<Radio size="small" />} label="Sim, excluir todos os agendamentos futuros" />
                    <FormControlLabel value="keep" control={<Radio size="small" />} label="Não, manter os agendamentos" />
                  </RadioGroup>
                </Box>
              )}

              <Alert severity="warning">
                Esta ação não pode ser desfeita. Planos cancelados, sessões removidas e agendamentos excluídos
                não voltam se o paciente for marcado como ATIVO depois.
              </Alert>
            </>
          ) : (
            <Typography variant="body2">
              O paciente volta a aparecer como <strong>ATIVO</strong>. Planos, sessões e agendamentos removidos
              anteriormente não são restaurados.
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            color={willInactivate ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={saving || !canConfirm}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {willInactivate ? 'Confirmar inativação' : 'Marcar como ATIVO'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientActivityStatusChip;
